import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PATHS } from './paths';

// Layouts
const AppShell = lazy(() => import('@app/layout/AppShell'));
const MapLayout = lazy(() => import('@app/layout/MapLayout'));
const FullScreenLayout = lazy(() => import('@app/layout/FullScreenLayout'));
const AdminLayout = lazy(() => import('@features/admin/layout/AdminLayout'));
const AdminGuard = lazy(() => import('@features/admin/layout/AdminGuard'));

// Auth pages
const LoginPage = lazy(() => import('@app/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@app/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@app/auth/ResetPasswordPage'));
const AuthCallbackPage = lazy(() => import('@app/auth/AuthCallbackPage'));

// Feature pages
const DashboardPage = lazy(() => import('@features/dashboard/DashboardPage'));
const VesselDetailPage = lazy(() => import('@features/vessels/VesselDetailPage'));
const CatalogPage = lazy(() => import('@features/catalog/CatalogPage'));
const BasketPage = lazy(() => import('@features/basket/BasketPage'));
const BasketRouteCreatePage = lazy(() => import('@features/basket/BasketRouteCreatePage'));
const BasketImportPage = lazy(() => import('@features/basket/BasketImportPage'));
const OrdersPage = lazy(() => import('@features/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('@features/orders/OrderDetailPage'));
const RouteCreatePage = lazy(() => import('@features/routes/RouteCreatePage'));
const RouteEditPage = lazy(() => import('@features/routes/RouteEditPage'));
const RouteDetailPage = lazy(() => import('@features/routes/RouteDetailPage'));
const RouteProductsPage = lazy(() => import('@features/routes/RouteProductsPage'));
const RequestUpdatePage = lazy(() => import('@features/updates/RequestUpdatePage'));

// Admin pages
const TenantsPage = lazy(() => import('@features/admin/pages/TenantsPage'));
const DistributorsPage = lazy(() => import('@features/admin/pages/DistributorsPage'));
const AgentsPage = lazy(() => import('@features/admin/pages/AgentsPage'));
const CompaniesPage = lazy(() => import('@features/admin/pages/CompaniesPage'));
const VesselsPage = lazy(() => import('@features/admin/pages/VesselsPage'));
const UsersPage = lazy(() => import('@features/admin/pages/UsersPage'));
const HoldingTemplatesPage = lazy(
  () => import('@features/holding-templates/pages/HoldingTemplatesPage'),
);

// Other pages
const NotFoundPage = lazy(() => import('./NotFoundPage'));

export const router = createBrowserRouter([
  // Auth routes (no layout)
  { path: PATHS.LOGIN, element: <LoginPage /> },
  { path: PATHS.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
  { path: PATHS.RESET_PASSWORD, element: <ResetPasswordPage /> },
  { path: PATHS.AUTH_CALLBACK, element: <AuthCallbackPage /> },

  // App routes (requires auth)
  {
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to={PATHS.DASHBOARD} replace /> },

      // Panel routes (map + side panel)
      {
        element: <MapLayout />,
        children: [
          { path: PATHS.DASHBOARD, element: <DashboardPage /> },
          { path: PATHS.VESSELS, element: null },

          // Vessel detail — VesselDetailPage is a layout with URL-synced tabs.
          // Route sub-pages render inside the Routes tab via <Outlet />.
          {
            path: PATHS.VESSEL_DETAIL,
            element: <VesselDetailPage />,
            children: [
              { index: true, element: <Navigate to="holdings" replace /> },
              // Tab routes — content rendered by VesselDetailPage directly,
              // entries needed so React Router matches the URL.
              { path: 'holdings', element: null },
              { path: 'orders', element: null },
              { path: 'orders/:orderId', element: null },
              { path: 'updates', element: null },
              { path: 'updates/request', element: <RequestUpdatePage /> },
              { path: 'routes', element: null },
              { path: 'profile', element: null },
              { path: 'account', element: null },
              // Route sub-pages — rendered via <Outlet /> inside the Routes tab.
              { path: 'routes/create', element: <RouteCreatePage /> },
              { path: 'routes/:routeId/edit', element: <RouteEditPage /> },
              { path: 'routes/:routeId', element: <RouteDetailPage /> },
              { path: 'routes/:routeId/products', element: <RouteProductsPage /> },
            ],
          },

          { path: PATHS.CATALOG, element: <CatalogPage /> },
          { path: PATHS.BASKET, element: <BasketPage /> },
          { path: PATHS.BASKET_ROUTE_CREATE, element: <BasketRouteCreatePage /> },
          { path: PATHS.BASKET_IMPORT, element: <BasketImportPage /> },
          { path: PATHS.ORDERS, element: <OrdersPage /> },
          { path: PATHS.ORDER_DETAIL, element: <OrderDetailPage /> },
        ],
      },

      // Fullscreen routes (no map)
      {
        element: <FullScreenLayout />,
        children: [
          { path: PATHS.SETTINGS, element: <div>Settings (coming soon)</div> },
        ],
      },

      // Admin routes — AdminGuard redirects non-admin users to dashboard.
      {
        path: 'admin',
        element: <AdminGuard><AdminLayout /></AdminGuard>,
        children: [
          { index: true, element: <Navigate to="tenants" replace /> },
          { path: 'tenants', element: <TenantsPage /> },
          { path: 'distributors', element: <DistributorsPage /> },
          { path: 'distributors/:tenantId', element: <DistributorsPage /> },
          { path: 'agents', element: <AgentsPage /> },
          { path: 'agents/:tenantId', element: <AgentsPage /> },
          { path: 'companies', element: <CompaniesPage /> },
          { path: 'companies/:tenantId', element: <CompaniesPage /> },
          { path: 'vessels', element: <VesselsPage /> },
          { path: 'vessels/:vesselId', element: <VesselsPage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'users/:userId', element: <UsersPage /> },
          { path: 'holding-templates', element: <HoldingTemplatesPage /> },
          { path: 'holding-templates/:templateId', element: <HoldingTemplatesPage /> },
        ],
      },

      // Catch-all
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
